<script>
import { mapActions, mapGetters } from "vuex";
import {
  ODataHeader,
  ODataProvider,
  ODataString,
  ODataDate,
  ODataDateTime,
} from "pbas-vue-components";
import OptOutsHeaders from "../headers/OptOutsHeaders";
import EditOptOutForm from "../components/OptOuts/EditOptOutForm.vue";
import { planSetter } from "../mixins/planSetter";
import FilePreviewBase64 from "../components/Claims/FilePreviewBase64.vue";
import OptOutStatusHistoryHeaders from "../headers/getOptOutStatusHistory";

export default {
  name: "OptOutsList",
  components: { EditOptOutForm, FilePreviewBase64 },
  mixins: [planSetter],
  props: {
    planId: String,
    totalCount: Number,
  },
  computed: {
    ...mapGetters("common", { selectedPlan: "plan" }),
    statusTemplateItems() {
      return [
        { text: "Received", value: "Received" },
        { text: "Pending", value: "Pending" },
        { text: "Rejected", value: "Rejected" },
        { text: "TentativelyApproved", value: "TentativelyApproved" },
        { text: "Approved", value: "Approved" },
        { text: "Updated", value: "Updated" },
        { text: "Cancelled", value: "Cancelled" },
        { text: "Paid", value: "Paid" },
      ];
    },
    coverageGroupItems() {
      return [{ name: "All", id: "" }, ...this.coverageGroups];
    },
    provider() {
      if (!this.selectedPlan?.Id) return null;
      const planId = this.selectedPlan.Id;

      let args = [`plan=${planId}`];

      if (this.statusFilter) {
        args.push(`status='${this.statusFilter}'`);
      }

      if (this.coverageFilter) {
        args.push(`coverage=${this.coverageFilter}`);
      }

      const url = `optouts/odata/OptOutRequests/ByPlan(${args.join(",")})`;
      return new ODataProvider(
        this.$axiosHttp,
        url,
        this.headers.filter((h) => !h.excludeFromOData)
      );
    },
  },
  data() {
    return {
      selected: {},
      statusFilter: "",
      coverageFilter: "",
      saving: false,
      emailTemplates: [],
      statusFilters: [
        { text: "All", value: "" },
        { text: "Tentatively Approved", value: "TentativelyApproved" },
        { text: "Updated", value: "Updated" },
        { text: "Pending", value: "Pending" },
        { text: "Approved", value: "Approved" },
        { text: "Rejected", value: "Rejected" },
        { text: "Canceled", value: "Canceled" },
      ],
      configuration: {},
      optOuts: undefined,
      coverageGroups: [],
      headers: null,
      previewCoverageGroups: [],
      allHeaders: OptOutsHeaders,
      statusHeaders: OptOutStatusHistoryHeaders,
      statusHeadersWithStatus: null,
      optOut: null,
      preview: false,
      fileItems: null,
      editDialogKey: 0,
      dialogs: {
        edit: false,
        view: false,
      },
      rowActions: [],
    };
  },
  methods: {
    ...mapActions("optouts", [
      "loadOptOuts",
      "updateOptOut",
      "loadCoverageGroups",
      "loadConfiguration",
      "adminUpdate",
      "getEmailTemplate",
    ]),
    ...mapActions("documentsService", ["getFile"]),

    async loadEmailTemplates() {
      if (!this.selectedPlan?.Id) return;
      // get the list of all statuses
      const statuses = this.statusTemplateItems.map((item) => item.value);
      this.emailTemplates = await Promise.all(
        statuses.map(async (status) => {
          const response = await this.getEmailTemplate({
            planId: this.selectedPlan.Id,
            status: status,
          });
          return response;
        })
      );
    },

    async load() {
      if (!this.selectedPlan?.Id) return;

      const coverageGroups = await this.loadCoverageGroups(
        this.selectedPlan.Id
      );
      this.configuration = await this.loadConfiguration(this.selectedPlan.Id);

      const baseHeaders = [...this.allHeaders];
      const statusBaseHeaders = [...this.statusHeaders];
      const dateStatusHeaders = [];
      const statusForStatusHeaders = [];
      const statusHeaders = [];
      const fileHeaders = [];
      const periodHeaders = [];

      const planCoverageGroups = coverageGroups.filter(
        (group, index, coverageGroups) =>
          coverageGroups.findIndex((g) => g.id === group.id) === index
      );
      this.previewCoverageGroups = planCoverageGroups;

      this.rowActions = [
        {
          name: "edit",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.selected = item;
            this.editDialogKey++;
            this.$nextTick(() => (this.dialogs.edit = true));
          },
        },
        ...this.previewCoverageGroups.map((c) => ({
          name: `preview-${c.id}`,
          icon: "visibility",
          title: `Preview Proof - ${c.name}`,
          visible: (item) => {
            return item.OptOuts?.some(
              (optOut) =>
                optOut.CoverageGroup?.Name === c.name &&
                optOut.OptOutFiles?.length > 0
            );
          },
          handler: (item) => {
            const optOut = item.OptOuts.find(
              (x) =>
                x.CoverageGroup?.Name === c.name && x.OptOutFiles?.length > 0
            );

            if (optOut) {
              this.previewProof(optOut);
            }
          },
        })),
      ];

      planCoverageGroups.forEach((c) => {
        statusHeaders.push(
          new ODataHeader({
            title: "Status " + c.name,
            sortable: false,
            searchable: false,
            type: new ODataString("OptOuts/Statuses/Status,EffectiveDate", {
              getHtml: (items) => {
                const optOut = items.OptOuts.find(
                  (x) => x.CoverageGroup.Name === c.name
                );
                if (optOut !== undefined && optOut.Statuses.length > 0) {
                  const latest = [...optOut.Statuses].sort(
                    (a, b) =>
                      new Date(b.EffectiveDate) - new Date(a.EffectiveDate)
                  )[0];
                  switch (latest.Status) {
                    case "Approved":
                      return (
                        `<div class="success font-weight-bold pa-2 white--text">` +
                        latest.Status +
                        `</div>`
                      );
                    case "Updated":
                      return (
                        `<div class="info font-weight-bold pa-2 white--text">` +
                        latest.Status +
                        `</div>`
                      );
                    case "Pending":
                      return (
                        `<div class="warning font-weight-bold pa-2 white--text">` +
                        latest.Status +
                        `</div>`
                      );
                    case "TentativelyApproved":
                      return (
                        `<div class="warning font-weight-bold pa-2 white--text">` +
                        latest.Status +
                        `</div>`
                      );
                    case "Cancelled":
                      return (
                        `<div class="error font-weight-bold pa-2 white--text">` +
                        latest.Status +
                        `</div>`
                      );
                    case "Rejected":
                      return (
                        `<div class="error font-weight-bold pa-2 white--text">` +
                        latest.Status +
                        `</div>`
                      );
                    default:
                      return (
                        `<div class="font-weight-bold pa-2">` +
                        latest.Status +
                        `</div>`
                      );
                  }
                }

                return "";
              },
              getValue: (items) => {
                const optOut = items.OptOuts.find(
                  (x) => x.CoverageGroup.Name === c.name
                );
                if (optOut && optOut.Statuses.length > 0) {
                  const latest = [...optOut.Statuses].sort(
                    (a, b) =>
                      new Date(b.EffectiveDate) - new Date(a.EffectiveDate)
                  )[0];
                  return latest.Status;
                }
                return "";
              },
            }),
          })
        );
      });
      dateStatusHeaders.push(
        new ODataHeader({
          title: "Time Stamp",
          sortable: false,
          searchable: false,
          visible: true,
          type: new ODataDateTime("TimeStamp", {
            getHtml: (item) => {
              if (item.TimeStamp) {
                return `<div class="pa-1">${this.$helpers.formatDateTime(
                  item.TimeStamp
                )}</div>`;
              }
              return "";
            },
          }),
        })
      );
      planCoverageGroups.forEach((c) => {
        statusForStatusHeaders.push(
          new ODataHeader({
            title: "Status " + c.name,
            sortable: false,
            visible: true,
            searchable: false,
            type: new ODataString("OptOut/CoverageGroup/Name", {
              getHtml: (item) => {
                if (item.OptOut.CoverageGroup.Name === c.name) {
                  switch (item.Status) {
                    case "Approved":
                      return (
                        `<div class="success font-weight-bold pa-1 white--text">` +
                        item.Status +
                        `</div>`
                      );
                    case "Updated":
                      return (
                        `<div class="info font-weight-bold pa-1 white--text">` +
                        item.Status +
                        `</div>`
                      );
                    case "Pending":
                      return (
                        `<div class="warning font-weight-bold pa-1 white--text">` +
                        item.Status +
                        `</div>`
                      );
                    case "TentativelyApproved":
                      return (
                        `<div class="warning font-weight-bold pa-1 white--text">` +
                        item.Status +
                        `</div>`
                      );
                    case "Cancelled":
                      return (
                        `<div class="error font-weight-bold pa-1 white--text">` +
                        item.Status +
                        `</div>`
                      );
                    case "Rejected":
                      return (
                        `<div class="error font-weight-bold pa-1 white--text">` +
                        item.Status +
                        `</div>`
                      );
                    default:
                      return (
                        `<div class="font-weight-bold pa-1">` +
                        item.Status +
                        `</div>`
                      );
                  }
                } else {
                  return "";
                }
              },
            }),
          })
        );
      });
      fileHeaders.push(
        new ODataHeader({
          title: "OptOut Proof",
          sortable: false,
          searchable: false,
          type: new ODataString("OptOuts/OptOutFiles/", {
            getHtml: (items) => {
              const optOut = items.OptOuts.find(
                (x) => x.OptOutFiles && x.OptOutFiles.length > 0
              );
              if (optOut) {
                return `
                  <div class="pa-2 d-flex align-center justify-center">
                    <i class="mdi mdi-file-eye-outline" style="font-size: 20px; color: #616161;"></i>
                  </div>
                `;
              }
              return "";
            },
          }),
        })
      );
      periodHeaders.push(
        new ODataHeader({
          title: "Period Name",
          sortable: false,
          searchable: false,
          type: new ODataString("OptOutPeriod/Name", {
            getHtml: (item) => {
              if (item.OptOutPeriod && item.OptOutPeriod.Name) {
                return `<div class="pa-2">${item.OptOutPeriod.Name}</div>`;
              }
              return `<div class="pa-2 grey--text">No Period Name</div>`;
            },
          }),
        })
      );

      this.headers = [
        ...fileHeaders,
        ...statusHeaders,
        ...baseHeaders,
        ...periodHeaders,
      ];
      this.statusHeadersWithStatus = [
        ...dateStatusHeaders,
        ...statusForStatusHeaders,
        ...statusBaseHeaders,
      ];

      this.coverageGroups = planCoverageGroups;
      this.configuration = await this.loadConfiguration(this.selectedPlan.Id);
      await this.loadEmailTemplates();
    },
    generateProvider(item) {
      const statusUrl = `optouts/odata/Status/ByRequest(request=${item.Id})`;

      let data = new ODataProvider(
        this.$axiosHttp,
        statusUrl,
        this.statusHeadersWithStatus
      );
      return data;
    },

    async previewProof(optOut) {
      if (!optOut.OptOutFiles || optOut.OptOutFiles.length === 0) {
        console.log("No files found for this opt-out");
        return;
      }

      const lastFile = optOut.OptOutFiles[optOut.OptOutFiles.length - 1];

      this.fileItems = {
        FileSegments: [
          {
            Base64: lastFile.Data,
            FileName: lastFile.FileName,
            FileExtension: this.$helpers
              .fileExtension(lastFile.FileName)
              .toLowerCase(),
            UploadName: lastFile.FileName,
          },
        ],
      };

      this.preview = true;
    },
    async update(update) {
      try {
        this.saving = true;
        await this.adminUpdate(update);
        this.$refs.optOuts.getDataAsync();
        this.optOut = null;
      } catch (error) {
        console.error(error);
        this.error = error;
        this.saving = false;
      } finally {
        this.saving = false;
        this.dialogs.edit = false;
      }
    },
  },
  async mounted() {
    if (this.selectedPlan?.Id) {
      await this.load();
    }
  },
  watch: {
    statusFilter: {
      handler() {
        this.$nextTick(() => {
          this.$refs.optOuts.getDataAsync();
        });
      },
    },
    coverageFilter: {
      handler() {
        this.$nextTick(() => {
          this.$refs.optOuts.getDataAsync();
        });
      },
    },
    selectedPlan: {
      deep: true,
      async handler(newPlan) {
        if (newPlan?.Id) {
          await this.load();
        }
      },
    },
  },
};
</script>

<template>
  <div>
    <material-card
      title="OptOuts"
      :color="$vuetify.theme.themes.light.secondary"
      icon="work"
      v-if="selectedPlan"
    >
      <v-row class="ml-3" style="max-width: 600px">
        <v-col>
          <v-select
            label="Status"
            v-model="statusFilter"
            :items="statusFilters"
            item-value="value"
            item-text="text"
          ></v-select>
        </v-col>
        <v-col>
          <v-select
            label="Coverage"
            v-model="coverageFilter"
            :items="coverageGroupItems"
            item-value="id"
            item-text="name"
          ></v-select>
        </v-col>
      </v-row>
      <pbas-data-table
        v-if="provider"
        :rowActions="rowActions"
        :exporters="$exporters"
        ref="optOuts"
        indentifier="OptOutsList"
        :expandedFloatActions="() => []"
        :canExpand="(item) => true"
        :showSelect="false"
        :showExpand="true"
        :expandedSortBy="'EffectiveDate'"
        :expandedSortDescending="true"
        :generateProviderForExpanded="(item) => generateProvider(item)"
        :dataProvider="provider"
        :sortBy="'CreatedAt'"
        :sortDescending="true"
      >
      </pbas-data-table>
    </material-card>
    <v-dialog
      scrollable
      v-model="dialogs.edit"
      max-width="700"
      @input="dialogs.edit = false"
    >
      <material-card
        v-if="selected"
        title="Update OptOut"
        :color="$vuetify.theme.themes.light.primary"
        icon="edit"
        style="overflow: auto"
      >
        <edit-optOut-form
          @save="update"
          @close="dialogs.edit = false"
          ref="editOptOutForm"
          :key="editDialogKey"
          :optOuts="selected.OptOuts"
          :saving="saving"
          :emailTemplates="emailTemplates"
        />
      </material-card>
    </v-dialog>
    <file-preview-base64
      :fullScreen="false"
      v-if="preview"
      :view="preview"
      :fileItems="fileItems"
      @close="preview = false"
    />
  </div>
</template>
